import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { 
  FileText, 
  Download, 
  Building2, 
  Shield, 
  Loader2,
  FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState({});
  const [supplierFilters, setSupplierFilters] = useState({ status: '', sector: '' });

  const downloadReport = async (endpoint, filename, reportKey) => {
    setLoading(prev => ({ ...prev, [reportKey]: true }));
    try {
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download report');
    } finally {
      setLoading(prev => ({ ...prev, [reportKey]: false }));
    }
  };

  const downloadSuppliersReport = () => {
    let endpoint = '/reports/suppliers-pdf';
    const params = [];
    if (supplierFilters.status) params.push(`status=${supplierFilters.status}`);
    if (supplierFilters.sector) params.push(`sector=${encodeURIComponent(supplierFilters.sector)}`);
    if (params.length > 0) endpoint += '?' + params.join('&');
    
    const filename = `suppliers_report_${new Date().toISOString().split('T')[0]}.pdf`;
    downloadReport(endpoint, filename, 'suppliers');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Generate and download PDF reports</p>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Suppliers Report */}
        <Card data-testid="suppliers-report-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Suppliers Report</CardTitle>
                <CardDescription>Complete list of all suppliers</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Filter by Status</Label>
                <Select 
                  value={supplierFilters.status} 
                  onValueChange={(value) => setSupplierFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="onboarded">Onboarded</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="do_not_use">Do Not Use</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              className="w-full" 
              onClick={downloadSuppliersReport}
              disabled={loading.suppliers}
              data-testid="download-suppliers-pdf"
            >
              {loading.suppliers ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
          </CardContent>
        </Card>

        {/* Compliance Report */}
        <Card data-testid="compliance-report-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Shield className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Compliance Report</CardTitle>
                <CardDescription>Document compliance overview</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Includes compliance status for all suppliers, missing documents, expired and expiring documents.
            </p>
            <Button 
              className="w-full" 
              onClick={() => downloadReport(
                '/reports/compliance-pdf',
                `compliance_report_${new Date().toISOString().split('T')[0]}.pdf`,
                'compliance'
              )}
              disabled={loading.compliance}
              data-testid="download-compliance-pdf"
            >
              {loading.compliance ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
          </CardContent>
        </Card>

        {/* CSV Export */}
        <Card data-testid="csv-export-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <FileDown className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <CardTitle className="text-lg">CSV Export</CardTitle>
                <CardDescription>Export data to spreadsheet</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Export all supplier data to CSV format for use in Excel or other spreadsheet applications.
            </p>
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => downloadReport(
                '/export/suppliers',
                `suppliers_export_${new Date().toISOString().split('T')[0]}.csv`,
                'csv'
              )}
              disabled={loading.csv}
              data-testid="download-csv"
            >
              {loading.csv ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <FileText className="w-8 h-8 text-muted-foreground flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-1">Individual Supplier Reports</h3>
              <p className="text-sm text-muted-foreground">
                You can also download detailed PDF reports for individual suppliers from their profile page. 
                Navigate to <strong>Suppliers → Select a supplier → Download PDF</strong> button.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
